import { z } from 'zod';

import { entityIdSchema, pageMetaSchema } from './common.js';
import {
  stockItemCountersSchema,
  stockItemCreateInputSchema,
  stockItemListQuerySchema,
  stockItemSchema,
  stockItemUpdateInputSchema,
  stockCanonicalIsoInstantSchema,
  stockItemNameSchema,
  stockLocationDescriptionSchema,
  stockLocationLabelSchema,
  stockQuantitySchema,
  stockScopeSchema,
} from './stock.js';

export const stockConfigurationKindSchema = z.enum(['category', 'unit', 'location']);
export const stockConfigurationStateSchema = z.enum(['active', 'archived', 'all']);
export const stockConfigurationLabelSchema = stockLocationLabelSchema;
export const stockConfigurationDescriptionSchema = stockLocationDescriptionSchema;
export const stockConfigurationDisplayOrderSchema = z.number().int().min(0).max(1_000_000);
export const stockAlertLevelSchema = z.enum(['LOW_STOCK', 'OUT_OF_STOCK']);

export const stockItemConfigurationCreateInputSchema = stockItemCreateInputSchema
  .omit({ alertThreshold: true })
  .extend({
    categoryId: entityIdSchema.nullable().default(null),
    unitId: entityIdSchema.nullable().default(null),
    alertThreshold: stockQuantitySchema.nullable().default(null),
  })
  .strict();

export const stockItemConfigurationUpdateInputSchema = stockItemUpdateInputSchema
  .omit({ alertThreshold: true })
  .extend({
    categoryId: entityIdSchema.nullable().optional(),
    unitId: entityIdSchema.nullable().optional(),
    alertThreshold: stockQuantitySchema.nullable().optional(),
  })
  .strict();

export const stockAlertSchema = z
  .object({
    itemId: entityIdSchema,
    itemName: stockItemNameSchema,
    scope: stockScopeSchema,
    categoryId: entityIdSchema.nullable(),
    locationId: entityIdSchema.nullable(),
    locationLabel: stockLocationLabelSchema.nullable(),
    available: stockQuantitySchema,
    threshold: stockQuantitySchema,
    level: stockAlertLevelSchema,
    calculatedAt: stockCanonicalIsoInstantSchema,
    detailHref: z.string().startsWith('/stock/'),
  })
  .strict();

export const stockItemReadModelSchema = stockItemSchema
  .extend({
    categoryId: entityIdSchema.nullable(),
    categoryName: stockConfigurationLabelSchema.nullable(),
    unitId: entityIdSchema.nullable(),
    alertThresholdOverride: stockQuantitySchema.nullable(),
    alert: stockAlertSchema.nullable(),
  })
  .strict();

export const stockItemReadModelListQuerySchema = stockItemListQuerySchema
  .extend({
    categoryId: entityIdSchema.optional(),
    alertLevel: stockAlertLevelSchema.optional(),
  })
  .strict();

export const stockItemReadModelCountersSchema = stockItemCountersSchema
  .extend({ outOfStock: z.number().int().nonnegative() })
  .strict();

export const stockItemReadModelPageSchema = z
  .object({
    items: z.array(stockItemReadModelSchema),
    page: pageMetaSchema,
    counters: stockItemReadModelCountersSchema,
  })
  .strict();

export const stockConfigurationEntrySchema = z
  .object({
    id: entityIdSchema,
    kind: stockConfigurationKindSchema,
    scope: stockScopeSchema.nullable(),
    label: stockConfigurationLabelSchema,
    description: stockConfigurationDescriptionSchema,
    displayOrder: stockConfigurationDisplayOrderSchema,
    active: z.boolean(),
    revision: z.number().int().positive(),
    createdAt: stockCanonicalIsoInstantSchema,
    updatedAt: stockCanonicalIsoInstantSchema,
  })
  .strict();

export const stockConfigurationSchema = z
  .object({
    scope: stockScopeSchema,
    defaultAlertThreshold: stockQuantitySchema,
    revision: z.number().int().positive(),
    categories: z.array(stockConfigurationEntrySchema),
    units: z.array(stockConfigurationEntrySchema),
    locations: z.array(stockConfigurationEntrySchema),
  })
  .strict();

export const stockConfigurationListQuerySchema = z
  .object({
    scope: stockScopeSchema,
    state: stockConfigurationStateSchema.default('active'),
  })
  .strict();

export const stockConfigurationCreateInputSchema = z
  .object({
    kind: stockConfigurationKindSchema,
    scope: stockScopeSchema.nullable().default(null),
    label: stockConfigurationLabelSchema,
    description: stockConfigurationDescriptionSchema.default(''),
    displayOrder: stockConfigurationDisplayOrderSchema.default(0),
  })
  .strict();

export const stockConfigurationUpdateInputSchema = z
  .object({
    label: stockConfigurationLabelSchema.optional(),
    description: stockConfigurationDescriptionSchema.optional(),
    displayOrder: stockConfigurationDisplayOrderSchema.optional(),
    revision: z.number().int().positive(),
  })
  .strict();

export const stockConfigurationStateInputSchema = z
  .object({ revision: z.number().int().positive() })
  .strict();

export const stockConfigurationSettingsInputSchema = z
  .object({
    defaultAlertThreshold: stockQuantitySchema,
    revision: z.number().int().positive(),
  })
  .strict();

export type StockConfigurationKind = z.infer<typeof stockConfigurationKindSchema>;
export type StockConfigurationState = z.infer<typeof stockConfigurationStateSchema>;
export type StockAlertLevel = z.infer<typeof stockAlertLevelSchema>;
export type StockAlert = z.infer<typeof stockAlertSchema>;
export type StockItemReadModel = z.infer<typeof stockItemReadModelSchema>;
export type StockItemConfigurationCreateInput = z.infer<
  typeof stockItemConfigurationCreateInputSchema
>;
export type StockItemConfigurationUpdateInput = z.infer<
  typeof stockItemConfigurationUpdateInputSchema
>;
export type StockItemReadModelListQuery = z.infer<typeof stockItemReadModelListQuerySchema>;
export type StockConfigurationEntry = z.infer<typeof stockConfigurationEntrySchema>;
export type StockConfiguration = z.infer<typeof stockConfigurationSchema>;
export type StockConfigurationListQuery = z.infer<typeof stockConfigurationListQuerySchema>;
export type StockConfigurationCreateInput = z.infer<typeof stockConfigurationCreateInputSchema>;
export type StockConfigurationUpdateInput = z.infer<typeof stockConfigurationUpdateInputSchema>;
export type StockConfigurationSettingsInput = z.infer<typeof stockConfigurationSettingsInputSchema>;
